import { useEffect, useRef, useState } from 'react';
import type { ErrorComponentProps } from '@tanstack/react-router';
import { Button } from '@workpay/ds-shared/components/ui/Button';
import { useTranslation } from '@hooks/useTranslation';
import AlertCircleIcon from '@assets/icons/AlertCircleIcon';
import styles from './AppErrorScreen.module.scss';

// Chrome stamps these on <html> once it has translated the page. Translation
// wraps React's text nodes in <font>, which breaks the next commit
// ("insertBefore … is not a child of this node") — the reason this screen
// exists. Checked lazily so it reads the DOM as it was at crash time.
function detectBrowserTranslation(): boolean {
  const cls = document.documentElement.classList;
  return (
    cls.contains('translated-ltr') ||
    cls.contains('translated-rtl') ||
    document.querySelector('#root font') !== null
  );
}

/** Router-wide fallback for uncaught render errors (`defaultErrorComponent`). */
export function AppErrorScreen({ error, info }: ErrorComponentProps) {
  const { t } = useTranslation();
  const [wasTranslated] = useState(detectBrowserTranslation);
  const headingRef = useRef<HTMLHeadingElement>(null);
  const message = error instanceof Error ? error.message : String(error);

  useEffect(() => {
    console.error(
      '[AppErrorScreen] Render error at',
      window.location.pathname,
      error,
      info?.componentStack,
    );
  }, [error, info]);

  // The crashed subtree took the user's focus with it — land on the heading so
  // AT announces the message and Tab reaches Reload in one press.
  useEffect(() => {
    headingRef.current?.focus();
  }, []);

  return (
    <main className={styles.page}>
      <div className={styles.card}>
        <AlertCircleIcon className={styles.icon} />
        <div className={styles.message} role="alert">
          <h1 ref={headingRef} tabIndex={-1} className={styles.title}>
            {t('K_DS_EXT_APP_ERROR_TITLE', 'Something went wrong')}
          </h1>
          <p className={styles.body}>
            {t(
              'K_DS_EXT_APP_ERROR_BODY',
              'This page ran into an unexpected problem. Reloading the page usually fixes it.',
            )}
          </p>
          {wasTranslated && (
            <p className={styles.hint}>
              {t(
                'K_DS_EXT_APP_ERROR_TRANSLATE_HINT',
                'Browser page translation was detected. It can interfere with this page, so please turn it off before reloading.',
              )}
            </p>
          )}
        </div>
        <Button variant="primary" size="lg" onClick={() => window.location.reload()}>
          {t('K_DS_EXT_ACTION_RELOAD_PAGE', 'Reload page')}
        </Button>
        <details className={styles.details}>
          <summary>{t('K_DS_EXT_APP_ERROR_DETAILS', 'Technical details')}</summary>
          <pre dir="ltr">{message}</pre>
        </details>
      </div>
    </main>
  );
}
